import { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const fmt = (n) =>
  n >= 10000000 ? `₹ ${(n / 10000000).toFixed(2)} Cr` : n >= 100000 ? `₹ ${(n / 100000).toFixed(2)} Lac` : `₹ ${Math.round(n).toLocaleString("en-IN")}`;

export const EmiCalculator = ({ price = 0 }) => {
  const [downPct, setDownPct] = useState(20);
  const [rate, setRate] = useState(8.5);
  const [years, setYears] = useState(20);

  const { emi, principal, totalInterest } = useMemo(() => {
    const p = Math.max(price * (1 - downPct / 100), 0);
    const r = rate / 12 / 100;
    const n = years * 12;
    const m = r > 0 ? (p * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1) : p / n;
    return { emi: m || 0, principal: p, totalInterest: (m || 0) * n - p };
  }, [price, downPct, rate, years]);

  if (!price) return null;

  return (
    <div className="rounded-2xl border border-border p-6" data-testid="emi-calculator">
      <h3 className="font-display text-lg">Home loan EMI</h3>
      <p className="mt-1 text-[13px] text-muted-foreground">An estimate — actual rates vary by lender and profile.</p>

      <p className="mt-5 text-3xl font-bold" data-testid="emi-monthly-value">
        {fmt(emi)}
        <span className="ml-1 text-sm font-medium text-muted-foreground">/ month</span>
      </p>

      <div className="mt-6 space-y-6">
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-xs font-semibold">Down payment</Label>
            <span className="text-xs font-bold" data-testid="emi-down-value">
              {downPct}% · {fmt(price * (downPct / 100))}
            </span>
          </div>
          <Slider
            value={[downPct]}
            min={0}
            max={80}
            step={5}
            onValueChange={(v) => setDownPct(v[0])}
            className="mt-3"
            data-testid="emi-down-slider"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Interest rate %</Label>
            <Input
              type="number"
              step="0.1"
              value={rate}
              onChange={(e) => setRate(Number(e.target.value || 0))}
              className="h-11 rounded-lg"
              data-testid="emi-rate-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Tenure (years)</Label>
            <Input
              type="number"
              value={years}
              onChange={(e) => setYears(Math.max(Number(e.target.value || 1), 1))}
              className="h-11 rounded-lg"
              data-testid="emi-years-input"
            />
          </div>
        </div>
      </div>

      <div className="mt-6 grid gap-2 border-t border-border pt-4 text-[13px]">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Loan amount</span>
          <span className="font-semibold" data-testid="emi-principal-value">{fmt(principal)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Total interest</span>
          <span className="font-semibold" data-testid="emi-interest-value">{fmt(Math.max(totalInterest, 0))}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Total payable</span>
          <span className="font-semibold">{fmt(principal + Math.max(totalInterest, 0))}</span>
        </div>
      </div>
    </div>
  );
};

export default EmiCalculator;
